import { animate, state, style, transition, trigger } from "@angular/animations";

/**
 * Expands/collapses the lane block below the total progress (same pattern as the Angular Material expansion panel).
 * The block element always exists, so a collapse that is interrupted by new lanes simply expands again from its
 * current height - unlike `:enter`/`:leave`, which would let a leaving and an entering block add up their heights.
 * The element needs `overflow: hidden` permanently.
 */
export const laneBlock = trigger("laneBlock", [
  state("collapsed", style({ height: "0px", opacity: 0, visibility: "hidden" })),
  state("expanded", style({ height: "*", opacity: 1, visibility: "visible" })),
  transition("collapsed => expanded", animate("250ms cubic-bezier(0.25, 0.8, 0.25, 1)")),
  transition("expanded => collapsed", animate("200ms cubic-bezier(0.4, 0, 0.2, 1)")),
]);

/**
 * Smoothly grows (and fades in) a sub-progress lane slot added to an already expanded lane block, so the dialog height
 * does not jump by a full lane.
 * Spacing between lanes must be padding of an element *inside* the animated one - the height animation would
 * otherwise leave margins/gaps behind at height 0.
 * Never let an entering and a leaving slot animate at the same time: their heights do not add up to a constant, so
 * the dialog would briefly grow by a third of a lane and snap back.
 */
export const laneReveal = trigger("laneReveal", [
  transition(":enter", [
    style({ height: 0, opacity: 0, overflow: "hidden" }),
    animate("250ms cubic-bezier(0.25, 0.8, 0.25, 1)", style({ height: "*", opacity: 1 })),
  ]),
]);

/**
 * Fades in a lane that takes over the slot of a finished lane. Starts at the finished lane's dimmed opacity
 * (`opacity-50`), so the swap does not blink.
 */
export const laneSwap = trigger("laneSwap", [
  transition(":enter", [style({ opacity: 0.5 }), animate("200ms ease-out", style({ opacity: 1 }))]),
]);
